/**
 * DOCTOR-OS-EXPERIENCE-V1 (B3) -- pure view-model for the treatment-consent
 * surface (ConsentPanel + the snapshot-header consent chip + the print-page
 * opener). React-free; unit-tested by direct call (repo idiom: no jsdom).
 *
 * DERIVED STATES ARE COMPUTED, NEVER STORED: the "consent owed" / "consent
 * signed" chips derive from the sheet's own accepted plan lines + the wire's
 * uncoveredAcceptedItemIds (the server's ONE derivation over the org's
 * consent.required_for_procedure config). This module maps tokens to labels
 * and tones only -- it never re-derives the requirement rule.
 */

import type {
  CaseSheetConsentsWire,
  ConsultPatientBanner,
  TreatmentConsentStateView,
  TreatmentConsentVoidReasonView,
  TreatmentConsentView,
} from '@/dashboard/api';
import type { AcBadgeTone } from '@/dashboard/ui';
import { parseConsultAppointmentId } from '@/dashboard/doctor/doctor-consult-view';

// --- Record-state labels + tones ---------------------------------------------

/** Display label per stored consent-record state (labels, never tokens). */
export function consentStateLabel(state: TreatmentConsentStateView): string {
  switch (state) {
    case 'generated':
      return 'Awaiting signature';
    case 'signed_digital':
      return 'Signed (digital)';
    case 'signed_physical_captured':
      return 'Signed (paper captured)';
    case 'voided_superseded':
      return 'Superseded';
  }
}

/** Chip tone per state (the seven-tone AcBadge law; no new colors). */
export function consentStateTone(state: TreatmentConsentStateView): AcBadgeTone {
  switch (state) {
    case 'generated':
      return 'pending';
    case 'signed_digital':
    case 'signed_physical_captured':
      return 'positive';
    case 'voided_superseded':
      return 'neutral';
  }
}

// --- Void / supersede reason vocabulary --------------------------------------

/** The controlled void-reason options (mirrors the backend CHECK 1:1; NEVER
 *  free text). Order = the select's render order. */
export const CONSENT_VOID_REASON_OPTIONS: ReadonlyArray<{
  readonly value: TreatmentConsentVoidReasonView;
  readonly label: string;
}> = [
  { value: 'wrong_patient', label: 'Wrong patient' },
  { value: 'wrong_treatment', label: 'Wrong treatment' },
  { value: 'wording_version_superseded', label: 'Wording version superseded' },
  { value: 'signer_error', label: 'Signer error' },
  { value: 'generated_in_error', label: 'Generated in error' },
  { value: 'other', label: 'Other' },
];

export function consentVoidReasonLabel(
  reason: TreatmentConsentVoidReasonView | null,
): string {
  if (reason === null) return '';
  return (
    CONSENT_VOID_REASON_OPTIONS.find((o) => o.value === reason)?.label ?? reason
  );
}

// --- Signer / card facts -----------------------------------------------------

/** The signer field value: "Patient", "Guardian (mother)", with the recorded
 *  name when present -- fields, not sentences; '' when nothing recorded. */
export function consentSignerLine(
  consent: Pick<
    TreatmentConsentView,
    'signerType' | 'signerName' | 'guardianRelationship'
  >,
): string {
  if (consent.signerType === null) return '';
  const who = consent.signerType === 'guardian' ? 'Guardian' : 'Patient';
  const rel =
    consent.signerType === 'guardian' && consent.guardianRelationship !== null
      ? ` (${consent.guardianRelationship})`
      : '';
  const name = consent.signerName !== null ? ` - ${consent.signerName}` : '';
  return `${who}${rel}${name}`;
}

// --- The uncovered-accepted glue ---------------------------------------------

/** True when THIS accepted plan line still owes a SIGNED consent. */
export function planLineConsentOwed(
  itemId: number,
  wire: Pick<CaseSheetConsentsWire, 'available' | 'uncoveredAcceptedItemIds'> | null,
): boolean {
  if (wire === null || !wire.available) return false;
  return wire.uncoveredAcceptedItemIds.includes(itemId);
}

/** The ACCEPTED plan lines (the only lines consent may name -- B3). */
export function acceptedPlanLines<
  T extends { readonly id: number; readonly status: string },
>(planItems: readonly T[]): readonly T[] {
  return planItems.filter((i) => i.status === 'accepted');
}

/** The generate modal's DEFAULT selection: the uncovered accepted lines
 *  (falling back to ALL accepted lines when nothing is owed -- a fresh
 *  document over already-covered lines stays lawful). */
export function defaultConsentSelection(
  planItems: ReadonlyArray<{ readonly id: number; readonly status: string }>,
  wire: Pick<CaseSheetConsentsWire, 'uncoveredAcceptedItemIds'> | null,
): readonly number[] {
  const accepted = acceptedPlanLines(planItems).map((i) => i.id);
  const uncovered = wire?.uncoveredAcceptedItemIds ?? [];
  const owed = accepted.filter((id) => uncovered.includes(id));
  return owed.length > 0 ? owed : accepted;
}

// --- The snapshot-header consent chip ----------------------------------------

export interface ConsentHeaderChip {
  readonly label: string;
  readonly tone: AcBadgeTone;
}

/**
 * The header chip derivation (spec A): none needed -> null (nothing);
 * uncovered accepted lines -> 'Consent owed' (warn); every required accepted
 * line covered by a SIGNED document -> 'Consent signed' (ok). "Needed" =
 * the wire is available AND the org requires consent AND at least one
 * accepted line exists.
 */
export function consentHeaderChip(
  planItems: ReadonlyArray<{ readonly id: number; readonly status: string }>,
  wire: Pick<
    CaseSheetConsentsWire,
    'available' | 'requiredForProcedure' | 'uncoveredAcceptedItemIds'
  > | null,
): ConsentHeaderChip | null {
  if (wire === null || !wire.available) return null;
  if (!wire.requiredForProcedure) return null;
  const accepted = acceptedPlanLines(planItems);
  if (accepted.length === 0) return null;
  if (wire.uncoveredAcceptedItemIds.length > 0) {
    return { label: 'Consent owed', tone: 'pending' };
  }
  return { label: 'Consent signed', tone: 'positive' };
}

// --- Signature capture -------------------------------------------------------

/** Strip the data-URL prefix off a canvas.toDataURL('image/png') payload,
 *  returning the BARE base64 the sign-digital wire expects. A bare base64
 *  string passes through unchanged; '' = nothing captured. */
export function signatureBase64FromDataUrl(dataUrl: string): string {
  const trimmed = dataUrl.trim();
  if (trimmed === '') return '';
  if (trimmed.startsWith('data:')) {
    const comma = trimmed.indexOf(',');
    return comma >= 0 ? trimmed.slice(comma + 1) : '';
  }
  return trimmed;
}

// --- The print-page opener ---------------------------------------------------

/**
 * Build the consent print route (spec F): the page renders the server-loaded
 * consent record + WHAT THE OPENER PASSED (patient identity + the named
 * treatment lines ride query params -- the consents wire itself is
 * PHI-minimal and carries neither patient identity nor procedure names).
 * NO PHI beyond the doctor's own lawful consult view rides the URL.
 */
export function consentPrintPath(args: {
  readonly caseSheetId: number;
  readonly consentId: number;
  /** Originating visit, for a deterministic return from a new print tab. */
  readonly appointmentId?: number;
  readonly patient: Pick<
    ConsultPatientBanner,
    'umr' | 'patientName' | 'ageYears' | 'gender'
  >;
  readonly treatmentNames: readonly string[];
}): string {
  const params = new URLSearchParams();
  params.set('caseSheetId', String(args.caseSheetId));
  params.set('consentId', String(args.consentId));
  const appointmentId = parseConsultAppointmentId(
    args.appointmentId === undefined ? undefined : String(args.appointmentId),
  );
  if (appointmentId !== null) params.set('appointmentId', String(appointmentId));
  if (args.patient.umr !== null && args.patient.umr !== '') {
    params.set('umr', args.patient.umr);
  }
  if (args.patient.patientName !== null && args.patient.patientName !== '') {
    params.set('name', args.patient.patientName);
  }
  if (args.patient.ageYears !== null) {
    params.set('age', String(args.patient.ageYears));
  }
  if (args.patient.gender !== null && args.patient.gender !== '') {
    params.set('sex', args.patient.gender);
  }
  for (const name of args.treatmentNames) {
    if (name.trim() !== '') params.append('t', name.trim());
  }
  return `/dashboard/patients/treatment-consent-print?${params.toString()}`;
}

/** The honest pre-apply line (the SeamChip-style absence copy). */
export const CONSENT_ENGINE_PENDING_COPY =
  'Consent engine pending migration 20260804_2';
